
import { Calculation } from "../models/Calculation";
import { CalculationField } from "../models/CalculationField";
import { CalculationFieldType } from "../models/CalculationFieldType";
import { CalculationSchema } from "../models/CalculationSchema";
import { CalculationsTypeGuardService } from "./CalculationsTypeGuardService";
import { QueueCustomCalculationsArgs } from "@/shared/Executions/models/QueueCustomCalculationsArgs";
import { QueueExecutionArgs } from "@/shared/Executions/models/QueueExecutionArgs";
import { IQueueExecutionRequest } from "@/shared/Executions/models/IQueueExecutionRequest";
import { QueueExecutionRequestFactory } from "@/shared/Executions/services/QueueExecutionRequestFactory";

export class CalculationsService {

    public static readonly CUSTOM_CALCULATION_TYPE = "unknown";

    public static getFlattenedCalculation(calculation: Calculation, includeType: string | null): Calculation {
        if (calculation == null) throw new Error("calculation is required");

        const calculationObject = calculation.configuration ?? calculation;
        const { _type, ...properties } = calculationObject || {};
        const flattenedCalculation = { ...properties } as Calculation;
        if (includeType != null) {
            flattenedCalculation.type = includeType;
        }
        return flattenedCalculation;
    }

    public static createCalculationFromCalculationSchema(calculationSchema: CalculationSchema): Calculation {
        const newCalculation: Calculation = {
            id: this.generateCalculationId(),
            _type: calculationSchema.type,
            display_name: calculationSchema.display_name,
            description: 'New calculation configuration',
            category: calculationSchema.category,
            configuration: {},
            schema: calculationSchema
        };
        return newCalculation;
    }

    public static createCalculationFromCalculationField(calculationField: CalculationField): Calculation {
        const newCalculation: Calculation = {
            id: this.generateCalculationId(),
            _type: calculationField.field_type,
            display_name: calculationField.display_name,
            display_field: calculationField.display_field,
            description: calculationField.description,
            category: "Default Value",
            configuration: {},
            schema: calculationField
        };
        return newCalculation;
    }

    private static generateCalculationId(): string {
        const id = `calculation-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        return id;
    }

    public static initializeCalculationConfiguration(calculation: Calculation, fields: Array<CalculationField>): Record<string, any> {
        const config: Record<string, any> = {};
        config.description = `${calculation.display_name ?? "Calculation"} Description`;
        fields.forEach((field: CalculationField) => {
            this.initializeCalculationConfigurationForField(field, config);
        });
        config._type = calculation._type;
        return config;
    }

    public static initializeCalculationFieldConfiguration(fields: Array<CalculationField>): Record<string, any> {
        const config: Record<string, any> = {};
        fields.forEach((field: CalculationField) => {
            this.initializeCalculationConfigurationForField(field, config);
        });
        return config;
    }

    public static initializeCalculationConfigurationForField(field: CalculationField, configuration?: Record<string, any>): Record<string, any> {
        const config: Record<string, any> = configuration || {};
        if (field.default_value !== undefined) {
            config[field.name] = field.default_value;
        } else if (field.field_type === CalculationFieldType.Boolean) {
            config[field.name] = false;
        } else if (
            field.field_type === CalculationFieldType.MultiSelect ||
            field.field_type === CalculationFieldType.ColumnMultiSelect ||
            field.field_type === CalculationFieldType.Array ||
            field.field_type === CalculationFieldType.ColumnMultiSelectOrCreate
        ) {
            config[field.name] = [];
        } else if (
            field.field_type === CalculationFieldType.Object ||
            field.field_type === CalculationFieldType.StringTemplate
        ) {
            config[field.name] = {};
        } else {
            config[field.name] = '';
        }
        return config;
    }

    public static scrubOutputFileName(name: string): string {
        const supportedExtensions = ['csv']

        for (const ext of supportedExtensions) {
            const suffix = `.${ext}`
            if (name.toLowerCase().endsWith(suffix)) {
                return name.slice(0, -suffix.length)
            }
        }

        return name
    }

    public static createQueueCalculationsArgs(
        parentExecutionId: string,
        flattenedCalculations: Array<Calculation>,
        sortColumns: Array<string>,
        name?: string): QueueCustomCalculationsArgs {

        const args = new QueueCustomCalculationsArgs(
            flattenedCalculations,
            sortColumns,
            parentExecutionId,
            name,
        )

        return args
    }

    public static getFormattedCalculationsJson(parentExecutionId: string, executionName: string | undefined, sortColumns: Array<string>, calculations: Array<Calculation>): IQueueExecutionRequest {
        const flattenedCalculations = this.getFlattenedCalculations(calculations);
        const args = CalculationsService.createQueueCalculationsArgs(parentExecutionId, flattenedCalculations, sortColumns, executionName);
        const queueExecutionArgs = new QueueExecutionArgs({ tenantId: '', userId: '', customCalculationsArgs: args });
        const payload = QueueExecutionRequestFactory.createQueueExecutionRequest(queueExecutionArgs);
        return payload;
    }

    public static getFlattenedCalculations(calculations: Array<Calculation>): Array<Calculation> {
        const flattenedCalculations = (calculations || []).map((x) => CalculationsService.getFlattenedCalculation(x, x._type));
        this.recursivelyFlattenCalculations(flattenedCalculations);
        return flattenedCalculations;
    }

    private static recursivelyFlattenCalculations(calculations: Array<Calculation | Object>, visited = new Set<any>()) {
        for (const calculation of calculations) {
            if (visited.has(calculation)) continue;
            visited.add(calculation);

            for (const key of Object.keys(calculation)) {
                const value = (calculation as any)[key];

                if (Array.isArray(value)) {
                    const containsSubCalculations =
                        value.length > 0 &&
                        value.every((item) => item && CalculationsTypeGuardService.isCalculation(item));

                    if (containsSubCalculations) {
                        const flattenedSubCalculations = value.map((subCalculation: Calculation) => {
                            const includeType = !this.isInternalSchemaType(subCalculation._type) ? subCalculation._type : null;
                            return this.getFlattenedCalculation(subCalculation, includeType);
                        });

                        (calculation as any)[key] = flattenedSubCalculations;
                        this.recursivelyFlattenCalculations(flattenedSubCalculations, visited);
                    }
                }
                else if (CalculationsTypeGuardService.isNonEmptyCalculationObject(value)) {

                    for (const nestedKey of Object.keys(value)) {
                        const nestedValue = value[nestedKey];

                        // Case 1: Direct calculation object
                        if (CalculationsTypeGuardService.isCalculation(nestedValue)) {
                            const includeType = !this.isInternalSchemaType(nestedValue._type) ? nestedValue._type : null;
                            const flattened = this.getFlattenedCalculation(nestedValue, includeType);
                            value[nestedKey] = flattened;

                            this.recursivelyFlattenCalculations([flattened], visited);
                        }

                        // Case 2: Another nested non-empty calculation-like object
                        else if (CalculationsTypeGuardService.isNonEmptyCalculationObject(nestedValue)) {
                            const coerced = nestedValue as Object;
                            this.recursivelyFlattenCalculations([coerced], visited);
                        }

                        else if (Array.isArray(nestedValue)) {
                            const calculations = this.extractCalculationsFromArray(nestedValue);
                            if (calculations.length > 0) {
                                const flattened = calculations.map(subCalculation => {
                                    const includeType = !this.isInternalSchemaType(subCalculation._type) ? subCalculation._type : null;
                                    return this.getFlattenedCalculation(subCalculation, includeType);
                                });
                                value[nestedKey] = flattened;
                                this.recursivelyFlattenCalculations(flattened, visited);
                            }
                        }
                    }
                }

            }
        }
    }

    private static extractCalculationsFromArray(arr: unknown[]): Calculation[] {
        return arr.filter(CalculationsTypeGuardService.isCalculation);
    }

    public static getFormattedCalculationsJsonString(parentExecutionId: string, executionName: string | undefined, sortColumns: Array<string>, calculations: Array<Calculation>, calculationsOnly: boolean = false): string {
        const payload = CalculationsService.getFormattedCalculationsJson(parentExecutionId, executionName, sortColumns, calculations);
        return JSON.stringify((calculationsOnly ? payload.customCalculations : payload), null, 2);
    }

    public static tryGetCalculationSchemaByType(type: string, calculationSchemas: Array<CalculationSchema>): CalculationSchema | undefined {
        if (type == null) throw new Error("type is required");
        if (calculationSchemas == null) throw new Error("calculationSchemas are required");
        return calculationSchemas.find(x => x.type === type) || calculationSchemas.find(x => x.type === CalculationsService.CUSTOM_CALCULATION_TYPE);
    }

    // If you have internal schema types for calculations, implement this
    public static isInternalSchemaType(type: string): boolean {
        const internalTypes = ['object', 'array'];
        return internalTypes.indexOf(type?.toLowerCase().trim()) !== -1;
    }


}
